import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { AgChartInstance, AgChartOptions } from "ag-charts-community";
import { AgCharts } from "ag-charts-react";
import { useTheme } from "next-themes";
import { SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { getDateForDay } from "./ClientSummaryReportHelpers";
import { IClientSummaryFilters } from "./IndexClientSummary";

function convertDataForPastSevenDays(data: any, dataFilters: any) {
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri"];

  // Step 1: filter to last 7 days
  const now = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(now.getDate() - 6); // includes today

  const recentData = data.filter((item: any) => {
    const d = new Date(item.dateTimeSent);
    if (d > dataFilters.range[1]) {
      return false;
    }
    return d >= sevenDaysAgo && d <= now;
  });

  // Step 2: tally counts for Mon–Fri
  const tallies: Record<string, number> = {
    Mon: 0,
    Tue: 0,
    Wed: 0,
    Thu: 0,
    Fri: 0,
  };

  const currentESIDs: string[] = [];

  recentData.forEach((item: IBaseAnalyticsCombined) => {
    const dayNum = new Date(item.dateTimeSent).getDay(); // 0=Sun, 1=Mon...
    if (!currentESIDs.includes(item.tradeID)) {
      currentESIDs.push(item.tradeID);
      if (dayNum >= 1 && dayNum <= 5) {
        const dayName = daysOfWeek[dayNum - 1];
        tallies[dayName]++;
      }
    }
  });

  // Step 3: convert to array of objects
  const weekData = daysOfWeek.map((day) => ({
    day,
    value: tallies[day],
  }));

  // Step 4: reorder so current day is at bottom
  const dayIndexMap: Record<number, number> = {
    1: 0, // Monday
    2: 1, // Tue
    3: 2, // Wed
    4: 3, // Thu
    5: 4, // Fri
    6: 4, // Sat -> Friday
    0: 4, // Sun -> Friday
  };

  const today = new Date().getDay();
  const currentIndex = dayIndexMap[today];

  const weekdayData = weekData.filter((d) => daysOfWeek.includes(d.day));

  const prevDays = Array.from({ length: 4 }, (_, i) => {
    const idx = (currentIndex - 4 + i + 5) % 5;
    return weekdayData[idx];
  });
  const finalArr = [...prevDays, weekData[currentIndex]];
  return finalArr;
}

const TradesPastSevenDays = ({
  data,
  dataFilters,
  setDataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: IClientSummaryFilters;
  setDataFilters: React.Dispatch<SetStateAction<IClientSummaryFilters>>;
}) => {
  const chartRef = useRef<AgChartInstance | null>(null);

  const { theme } = useTheme();

  const [filteredData, setFilteredData] = useState(
    convertDataForPastSevenDays(data, dataFilters),
  );

  useEffect(() => {
    setFilteredData(convertDataForPastSevenDays(data, dataFilters));
    if (dataFilters.activeDay == undefined) {
      setSelectedDatum(undefined);
    }
  }, [data, dataFilters]);

  const [selectedDatum, setSelectedDatum] = useState<any>(undefined);

  useEffect(() => {
    if (selectedDatum?.day) {
      const sevenDayFilter = getDateForDay(selectedDatum.day) || undefined;
      setDataFilters({ ...dataFilters, activeDay: sevenDayFilter });
    } else {
      setDataFilters({ ...dataFilters, activeDay: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatum]);

  function handleBackgroundClick() {
    if (dataFilters) {
      setDataFilters({ ...dataFilters, activeDay: undefined });
    }
  }

  const options = useMemo<AgChartOptions | any>(() => {
    return {
      background: {
        visible: false,
      },
      width: 220,
      data: filteredData,
      theme: theme == "dark" ? "ag-default-dark" : "ag-default",
      series: [
        {
          type: "bar",
          xKey: "day",
          yKey: "value",
          fill: "#FC6A08",
          yName: "Trades",
          xName: "Trades",
          listeners: {
            nodeClick: (event: any) => {
              const clicked = event.datum;
              // If clicking the same bar again → deselect
              setSelectedDatum((prev: any) =>
                prev?.day === clicked.day ? null : clicked,
              );
            },
          },
          itemStyler: (params: any) => {
            const isSelected =
              selectedDatum && params.datum.day === selectedDatum.day;
            return {
              fillOpacity: isSelected ? 1 : 0.3, // dim others
            };
          },
          direction: "horizontal",
        },
      ],
      listeners: {
        click: (event: any) => {
          // If datum is undefined, click was not on a node
          if (!event.datum) {
            handleBackgroundClick();
          }
        },
      },
      legend: {
        position: "bottom",
        enabled: false,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatum, theme, filteredData]);

  return (
    <div className="h-full overflow-hidden  px-2">
      <div className="text-md h-fit">Trades Past 7 Days:</div>
      {selectedDatum && selectedDatum.day && (
        <div className="absolute flex pl-2 text-xs text-gray-500 dark:text-gray-500">
          Filtering: {selectedDatum.day}
        </div>
      )}
      <div className="relative right-7 flex h-full min-h-[150px] justify-start pb-4 ">
        <AgCharts ref={chartRef} options={options} />
      </div>
    </div>
  );
};

export default TradesPastSevenDays;
